import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const JOB_TYPES = ["Fitment", "Reinstall", "Deinstall", "Repair"];

export default function Installations() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ serial: "", channel: "", work_order_no: "", job_type: "Fitment", site_id: "" });

  const canCapture = ["fitment_technician", "operations_controller", "system_admin"].includes(user.role);

  const load = useCallback(() => { api.get("/installations").then((r) => setList(r.data)); }, []);
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setSites(r.data.filter((l) => ["fitment_centre", "technician"].includes(l.type))));
  }, [load]);

  const capture = async () => {
    const needsSite = ["Fitment", "Reinstall"].includes(form.job_type);
    if (!form.serial || !form.channel || !form.work_order_no || (needsSite && !form.site_id)) {
      toast.error("Serial, channel, work order" + (needsSite ? " and site" : "") + " required"); return;
    }
    try {
      const { data } = await api.post("/installations", form);
      toast.success(`${form.job_type} captured`, { description: data.outcome });
      setForm({ serial: "", channel: "", work_order_no: "", job_type: "Fitment", site_id: "", replacement_serial: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const JOB_HINTS = {
    Fitment: "Consumes a consignment unit → awaits IP2 activation.",
    Reinstall: "Relocates an already-active unit — no new draw-down, no re-bill.",
    Deinstall: "Removes the unit from service → sent to Returns (no activation).",
    Repair: "Faulty unit returned; fit a replacement as a new Fitment.",
  };

  return (
    <div>
      <PageHeader title="Installations" subtitle="Installer captures the channel and serial of the unit applied against a work order." />
      {canCapture && (
        <Panel title="Capture Fitment" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div><Label className="overline text-slate-500">Serial (at site)</Label><Input data-testid="inst-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className="rounded-sm mt-1 font-label" /></div>
            <div><Label className="overline text-slate-500">Channel</Label><Input data-testid="inst-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} placeholder="VBU / Camera" className="rounded-sm mt-1" /></div>
            <div><Label className="overline text-slate-500">Work Order No.</Label><Input data-testid="inst-wo" value={form.work_order_no} onChange={(e) => setForm({ ...form, work_order_no: e.target.value })} placeholder="WO-00001234" className="rounded-sm mt-1" /></div>
            <div><Label className="overline text-slate-500">Job Type</Label><div className="mt-1"><Select testid="inst-jobtype" value={form.job_type} onChange={(v) => setForm({ ...form, job_type: v })} options={JOB_TYPES.map((j) => ({ value: j, label: j }))} placeholder="Job type" /></div></div>
            <div><Label className="overline text-slate-500">Site</Label><div className="mt-1"><Select testid="inst-site" value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="Site" /></div></div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button data-testid="inst-submit" onClick={capture} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Capture Fitment</Button>
            <span className="text-xs text-slate-500">{JOB_HINTS[form.job_type]}</span>
          </div>
          {form.job_type === "Repair" && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <Label className="overline text-slate-500">Replacement serial (optional — fits a new unit in one step)</Label>
              <Input data-testid="inst-replacement" value={form.replacement_serial || ""} onChange={(e) => setForm({ ...form, replacement_serial: e.target.value })}
                placeholder="Serial at site to fit as replacement" className="rounded-sm mt-1 font-label max-w-md" />
            </div>
          )}
        </Panel>
      )}
      <Panel title="Installations">
        {list.length === 0 ? <Empty>No installations captured yet.</Empty> : (
          <table className="w-full text-sm" data-testid="installations-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Channel</th><th className="py-2 pr-3">WO</th><th className="py-2 pr-3">Job</th><th className="py-2 pr-3">Site</th><th className="py-2 pr-3">Status</th></tr></thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-label text-[13px]">{i.serial}</td>
                  <td className="py-2 pr-3 text-slate-600">{i.channel}</td>
                  <td className="py-2 pr-3 font-label text-xs">{i.work_order_no}</td>
                  <td className="py-2 pr-3">{i.job_type}</td>
                  <td className="py-2 pr-3 text-slate-600">{i.site_name}</td>
                  <td className="py-2 pr-3"><StatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
